import React, { useState, useEffect } from 'react';
import api from '../api';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Grid,
  Button,
  Stack,
  Container,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

function TrendsPage() {
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [monthsBack, setMonthsBack] = useState(6);

  useEffect(() => {
    fetchTrends();
  }, [monthsBack]);

  const fetchTrends = async () => {
    setLoading(true);
    setError(null);

    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - monthsBack);

      const response = await api.get('/api/analytics/trends', {
        params: {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        }
      });

      setTrends(response.data);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load trends');
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getTrendColors = (direction) => {
    if (direction === 'increasing') return { bg: 'rgba(239,68,68,0.07)', border: '#ef4444', chipBg: 'rgba(239,68,68,0.15)', chipColor: '#f87171' };
    if (direction === 'decreasing') return { bg: 'rgba(34,197,94,0.07)', border: '#22c55e', chipBg: 'rgba(34,197,94,0.15)', chipColor: '#4ade80' };
    return { bg: 'rgba(99,102,241,0.07)', border: '#6366f1', chipBg: 'rgba(99,102,241,0.15)', chipColor: '#818cf8' };
  };

  if (loading) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#6366f1' }} />
          </Box>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
        <Container maxWidth="lg">
          <Alert severity="error">{error}</Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
          <Typography variant="h5" fontWeight={700} color="text.primary">
            Spending Trends
          </Typography>
          <Stack direction="row" spacing={1}>
            {[3, 6, 12].map((n) => (
              <Button
                key={n}
                variant={monthsBack === n ? 'contained' : 'outlined'}
                onClick={() => setMonthsBack(n)}
                sx={{
                  borderColor: '#6366f1',
                  color: monthsBack === n ? 'white' : '#6366f1',
                  bgcolor: monthsBack === n ? '#6366f1' : 'rgba(255,255,255,0.05)',
                  '&:hover': {
                    bgcolor: monthsBack === n ? '#4f46e5' : 'rgba(99,102,241,0.12)',
                    borderColor: '#6366f1'
                  },
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 1.5
                }}
              >
                {n} Months
              </Button>
            ))}
          </Stack>
        </Stack>

        {/* Line Chart */}
        <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 0 0 1px rgba(255,255,255,0.05), 0 2px 8px rgba(0,0,0,0.3)', mb: 4 }}>
          <Typography variant="h6" fontWeight={600} color="text.primary" mb={2}>
            Category Trends Over Time
          </Typography>
          <Box sx={{ position: 'relative', height: 400 }}>
            <Line data={trends.charts.line} options={trends.charts.line.options} />
          </Box>
        </Paper>

        {/* Trend Analysis Cards */}
        <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 0 0 1px rgba(255,255,255,0.05), 0 2px 8px rgba(0,0,0,0.3)' }}>
          <Typography variant="h6" fontWeight={600} color="text.primary" mb={3}>
            Trend Analysis
          </Typography>
          <Grid container spacing={2}>
            {Object.entries(trends.trends.categoryTrends).map(([category, trend]) => {
              const colors = getTrendColors(trend.direction);
              return (
                <Grid item xs={12} sm={6} md={4} key={category}>
                  <Card sx={{
                    borderRadius: 2,
                    bgcolor: colors.bg,
                    borderLeft: `4px solid ${colors.border}`,
                    border: `1px solid ${colors.border}20`,
                    borderLeftWidth: 4,
                    boxShadow: 'none'
                  }}>
                    <CardContent sx={{ pb: '16px !important' }}>
                      <Typography variant="body1" fontWeight={600} color="text.primary" mb={0.75}>
                        {category}
                      </Typography>
                      <Box mb={1}>
                        <Chip
                          label={
                            trend.direction === 'increasing' ? '📈 Increasing'
                            : trend.direction === 'decreasing' ? '📉 Decreasing'
                            : '➡️ Stable'
                          }
                          size="small"
                          sx={{ bgcolor: colors.chipBg, color: colors.chipColor, fontWeight: 600, fontSize: '0.75rem' }}
                        />
                      </Box>
                      {trend.values.length > 0 && (
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">
                            Latest: {formatCurrency(trend.values[trend.values.length - 1])}
                          </Typography>
                          {trend.percentageChanges.length > 0 && (
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              color={colors.chipColor}
                            >
                              {trend.percentageChanges[trend.percentageChanges.length - 1] > 0 ? '+' : ''}
                              {trend.percentageChanges[trend.percentageChanges.length - 1].toFixed(1)}%
                            </Typography>
                          )}
                        </Stack>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
}

export default TrendsPage;
